import { useEffect, useRef, useState } from "react";
import { Microphone, MicrophoneSlash, StopCircle } from "@phosphor-icons/react";
import { voiceApi } from "@/lib/api";
import { toast } from "sonner";

/**
 * Voice input button. Click once to start, click again to stop.
 * Calls onTranscript({text, language}) when done.
 */
export default function VoiceButton({ onTranscript, language, testid = "voice-mic-button", compact = false }) {
    const [recording, setRecording] = useState(false);
    const [processing, setProcessing] = useState(false);
    const mediaRef = useRef(null);
    const chunksRef = useRef([]);

    const toggle = async () => {
        if (processing) return;
        if (recording) {
            mediaRef.current?.stop();
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
            const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
            chunksRef.current = [];
            rec.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };
            rec.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                setRecording(false);
                setProcessing(true);
                try {
                    const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
                    const { text, language: detected } = await voiceApi.transcribe(blob, language);
                    if (!text) {
                        toast.error("Could not transcribe. Try again.");
                    } else {
                        onTranscript({ text, language: detected });
                    }
                } catch (e) {
                    console.error(e);
                    toast.error("Voice transcription failed");
                } finally {
                    setProcessing(false);
                }
            };
            rec.start();
            mediaRef.current = rec;
            setRecording(true);
        } catch (e) {
            console.error(e);
            toast.error("Microphone permission denied");
        }
    };

    const size = compact ? 16 : 20;
    const cls = compact
        ? `mic-btn p-2 ${recording ? "recording" : ""} ${processing ? "opacity-60" : ""}`
        : `mic-btn ${recording ? "recording" : ""} ${processing ? "opacity-60" : ""}`;

    return (
        <button type="button" onClick={toggle} disabled={processing} data-testid={testid} className={cls} title={recording ? "Stop recording" : "Record voice"}>
            {processing ? (
                <MicrophoneSlash size={size} />
            ) : recording ? (
                <StopCircle size={size} weight="fill" />
            ) : (
                <Microphone size={size} weight="duotone" />
            )}
        </button>
    );
}
